import { defineStore } from 'pinia';
import type { Ref } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import { Permission, Role } from '~/roles/types';
import { Company } from '~/companies/types';
import { Feature } from '~/features/types';

export interface UserStore {
  isAuthenticated: boolean;
  accessToken: string;
  id: string;
  roles: string[];
  permissions: string[];
  features: Record<string, any>;
  company: string | null;
  unauthenticate: () => void;
  updateWithToken: (token: string) => void;
  setRoles: (roles: Role[]) => void;
  setPermissions: (permissions: Permission[]) => void;
  setFeatures: (features: Feature[]) => void;
  setCompany: (Company: Company | null) => void;
}

export const useUserStore = defineStore('user', () => {
  const isAuthenticated: Ref<boolean> = useLocalStorage(
    'isAuthenticated',
    false,
  );
  const accessToken: Ref<string> = useLocalStorage('accessToken', '');
  const id: Ref<string> = useLocalStorage('id', '');
  const roles: Ref<string[]> = useLocalStorage('roles', []);
  const permissions: Ref<string[]> = useLocalStorage('permissions', []);
  const features: Ref<Record<string, any>> = useLocalStorage('features', {});
  const company: Ref<string | null> = useLocalStorage('company', null);

  function unauthenticate() {
    isAuthenticated.value = false;
    accessToken.value = '';
    id.value = '';
    roles.value = [];
    permissions.value = [];
    features.value = {};
    company.value = null;
  }

  function updateWithToken(token: string) {
    isAuthenticated.value = true;
    accessToken.value = token;
  }

  function setRoles(newRoles: Role[]) {
    roles.value = newRoles.map((role) => role.name);
  }

  function setPermissions(newPermissions: Permission[]) {
    permissions.value = newPermissions.map((permission) => permission.name);
  }

  function setFeatures(newFeatures: Feature[]) {
    features.value = {};
    newFeatures.forEach((feature) => {
      features.value[feature.name] = {
        featureID: feature.featureID,
        settings: feature.settings?.data,
      };
    });
  }

  function setCompany(newCompany: Company | null) {
    company.value = JSON.stringify(newCompany);
  }

  return {
    accessToken,
    id,
    isAuthenticated,
    roles,
    permissions,
    features,
    company,
    unauthenticate,
    updateWithToken,
    setRoles,
    setPermissions,
    setFeatures,
    setCompany,
  };
});
